import { Result, Schema } from 'effect';

import { ONBOARDING_BASE_PATH } from '@finnest/power-admin-contracts';
import type { PhaseId } from '@finnest/power-admin-contracts';

import { rejectHttp } from './problem-details.ts';

type PowerAdminFetcher = (input: string, init?: RequestInit) => Promise<Response>;

export type OnboardingApiOptions = {
  readonly apiBaseUrl?: string;
  readonly fetcher?: PowerAdminFetcher;
  readonly requestInit?: RequestInit;
};

export const onboardingApiPaths = {
  overview: `${ONBOARDING_BASE_PATH}/overview`,
  phase: (phaseId: PhaseId) => `${ONBOARDING_BASE_PATH}/phases/${phaseId}`,
  evidenceRun: (phaseDir: string, runId: string) =>
    `${ONBOARDING_BASE_PATH}/evidence/${phaseDir}/${runId}`,
  inviteOwner: `${ONBOARDING_BASE_PATH}/p0/invite-owner`,
  skipSso: `${ONBOARDING_BASE_PATH}/p0/skip-sso`,
  certUpload: `${ONBOARDING_BASE_PATH}/p1/cert-upload`,
  ps256Generate: `${ONBOARDING_BASE_PATH}/p1/ps256-generate`,
  ssaFetch: `${ONBOARDING_BASE_PATH}/p1/ssa-fetch`,
  directoryRegistration: `${ONBOARDING_BASE_PATH}/p1/directory-registration`,
  directorySync: `${ONBOARDING_BASE_PATH}/p1/directory-sync`,
  kcValidateRealm: `${ONBOARDING_BASE_PATH}/p1/kc-validate-realm`,
  asMetadata: `${ONBOARDING_BASE_PATH}/p1/as-metadata`,
  dcrProbe: `${ONBOARDING_BASE_PATH}/p1/dcr-probe`,
  p1Smoke: `${ONBOARDING_BASE_PATH}/p1/smoke`,
  counterpartyContacts: `${ONBOARDING_BASE_PATH}/p1/counterparty-contacts`,
  counterpartyContactsList: `${ONBOARDING_BASE_PATH}/p1/counterparty-contacts`,
  directoryCertificates: `${ONBOARDING_BASE_PATH}/p1/directory-certificates`,
  requestReview: `${ONBOARDING_BASE_PATH}/review-requests`,
} as const;

export const apiUrl = (path: string, options: OnboardingApiOptions): string =>
  `${options.apiBaseUrl ?? ''}${path}`;

export const fetcherFor = (options: OnboardingApiOptions): PowerAdminFetcher =>
  options.fetcher ?? fetch;

export const jsonPostInit = <T>(
  body: T,
  options: OnboardingApiOptions,
  serialize: (body: T) => string = JSON.stringify,
): RequestInit => {
  const headers = new Headers(options.requestInit?.headers);
  headers.set('content-type', 'application/json');
  return {
    ...options.requestInit,
    method: 'POST',
    headers,
    body: serialize(body),
  };
};

// A create the server stores once per key: `idempotencyKey` names this logical mutation, and
// every retry of it repeats the key so that a lost response replays instead of creating again.
export const withIdempotencyKey = (init: RequestInit, idempotencyKey: string): RequestInit => {
  const headers = new Headers(init.headers);
  headers.set('idempotency-key', idempotencyKey);
  return { ...init, headers };
};

export const formPostInit = (body: FormData, options: OnboardingApiOptions): RequestInit => ({
  ...options.requestInit,
  method: 'POST',
  body,
});

export const getInit = (options: OnboardingApiOptions): RequestInit => ({
  ...options.requestInit,
  method: options.requestInit?.method ?? 'GET',
});

export const readJsonData = async <T>(
  response: Response,
  schema: Schema.Decoder<T>,
): Promise<T> => {
  if (!response.ok) return await rejectHttp(response);
  const decoded = Schema.decodeResult(Schema.fromJsonString(Schema.Struct({ data: schema })))(
    await response.text(),
  );
  if (Result.isFailure(decoded)) return await Promise.reject(new Error(decoded.failure.message));
  return decoded.success.data;
};
